import React, {useContext, useEffect, useRef, useState} from 'react';
import {FaRotateRight, FaTriangleExclamation} from "react-icons/fa6";
import {MaterialMovementsContext} from "../material-movements.context.jsx";
import projectUtils, {markRequiredFields} from "../../../../../utils/project-utils.js";
import {Modal} from "../../../../../components/modal/modal.jsx";
import SelectComponent from "../../../../../components/select/select-component.jsx";
import {useQuery} from "@tanstack/react-query";
import materialKitService from "../../../../../services/web/material-kit-service.js";
import Button from "../../../../../components/button/button.jsx"
import {FaPlus, FaTrash} from "react-icons/fa";
import materialXStorageService from "../../../../../services/web/material-x-storage-service.js";
import InputForm from "../../../../../components/input/input-form.jsx";
import InputCheckbox from "../../../../../components/input/input-checkbox.jsx";
import materialAlertService from "../../../../../services/web/material-alert-service.js";
import userService from "../../../../../services/web/user-service.js";
import useHasPermission from "../../../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../../../utils/enums/permission-enum.js";
import materialMovementService from "../../../../../services/web/material-movement-service.js";
import {useGlobalKey} from "../../../../../hooks/use-global-key.js";
import {endOfDay, isAfter, startOfDay} from "date-fns";
import SentryRow from "../../../../../components/template/sentry-row.jsx";

const initialMaterial = {
    material_x_storage_id: "",
    quantity: "",
    observations: "",
    permanent: false,
    is_unique: null,
    is_caution: null,
    breakdown: "",
    alertQuantity: null
}

let confirmCanCautionWithExpiredDocument = false
const Caution = ({storageSelected}) => {
    const {userSelected, refetchCautions, setIsLoading} = useContext(MaterialMovementsContext)
    const [openModal, setOpenModal] = useState(false)
    const [kitSelected, setKitSelected] = useState([])
    const [materials, setMaterials] = useState([])
    const [password, setPassword] = useState("")
    const [removeByThirdParty, setRemoveByThirdParty] = useState(false)
    const [thirdPartyRequester, setThirdPartyRequester] = useState("")
    const autoSelectedRef = useRef(false)
    const saveButtonRef = useRef(null)
    const passwordInputRef = useRef(null)

    useEffect(() => {
        if (!openModal) {
            setMaterials([])
            setKitSelected([])
            setPassword("")
            setRemoveByThirdParty(false)
            setThirdPartyRequester("")
            confirmCanCautionWithExpiredDocument = false
            autoSelectedRef.current = false
        }
    }, [openModal]);

    useEffect(() => {
        if (!removeByThirdParty) {
            setThirdPartyRequester("")
        }
    }, [removeByThirdParty]);

    const {data: kits, isLoading: isLoadingKits} = useQuery({
        queryKey: ['materials-kits'],
        queryFn: () => materialKitService.optionsByStorage(storageSelected.value),
        enabled: openModal
    });

    const {data: dataMaterial, isLoading: isLoadingMaterials} = useQuery({
        queryKey: ['materials'],
        queryFn: () => materialXStorageService.optionsAvailableByStorage(storageSelected.value),
        enabled: !!storageSelected && openModal
    });

    const {data: dataThirdPartyRequester, isLoading: isLoadingThirdPartyRequesters} = useQuery({
        queryKey: ['third-party-requesters'],
        queryFn: () => userService.optionsWithoutId(userSelected.value),
        enabled: !!userSelected && openModal
    });

    const getMaterialOptions = (currentValue) => {
        const baseOptions = dataMaterial || [];

        const selectedValues = materials.map(m => m.material_x_storage_id?.value).filter(Boolean);

        return baseOptions.filter(opt => opt.value === currentValue?.value || !selectedValues.includes(opt.value));
    }

    useEffect(() => {
        setIsLoading(isLoadingMaterials || isLoadingThirdPartyRequesters || isLoadingKits)
    }, [isLoadingMaterials, isLoadingThirdPartyRequesters, isLoadingKits]);

    const showModal = () => {
        if (!userSelected) {
            projectUtils.swalMessage('warning', '', `Selecione um solicitante.`, 'Ok');
            return;
        }

        setOpenModal(true)
        setMaterials([initialMaterial])
    }

    useEffect(() => {
        if (openModal && !autoSelectedRef.current && materials.length === 1 && dataMaterial?.length === 1 && !materials[0].material_x_storage_id) {
            autoSelectedRef.current = true
            onChange(0, "material_x_storage_id", dataMaterial[0]).then(() => {});
        }
    }, [openModal, materials, dataMaterial]);

    const addKit = async () => {
        setIsLoading(true)
        try {
            const kits = kitSelected.map(k => k.value)
            const data = await materialXStorageService.getByKits({kits, storage: storageSelected.value})

            if (data.length === 0) {
                projectUtils.swalMessage("warning", "Aviso", "Nenhum material disponível.", "Ok")
                return
            }

            const hasMaterialGun = data.some(i => i.is_gun)
            if (hasMaterialGun) {
                const canProceed = await handleUserCanCaution(true)
                if (!canProceed) {
                    return
                }
                confirmCanCautionWithExpiredDocument = true
            }

            await addMaterialsFromKits(data)
        } catch (error) {
            console.error(error)
            projectUtils.swalErrorMessage('', "Não foi possível carregar", 'Ok')
        } finally {
            setIsLoading(false)
        }
    }

    const addMaterialsFromKits = async (data) => {
        const newMaterials = []
        for (const item of data) {
            const {id, quantity} = item
            const findAddMaterials = materials.find(m => m.material_x_storage_id?.value === id)
            if (!findAddMaterials) {
                const materialXStorage = {value: id}
                const extraData = await handleMaterial(materialXStorage, quantity)
                if (!extraData) {
                    continue
                }
                extraData.quantity = parseInt(quantity)
                const newMaterial = {
                    ...initialMaterial,
                    material_x_storage_id: materialXStorage,
                    ...extraData
                }

                newMaterials.push(newMaterial)
            }
        }

        setMaterials(prevState => ([...prevState, ...newMaterials]))
    }

    const addMaterial = () => {
        setIsLoading(true)

        const filteredValues = getMaterialOptions("")
        if (filteredValues.length === 1) {
            setTimeout(async () => {
                try {
                    const index = filteredValues.length
                    const extraData = await handleMaterial(filteredValues[0], 0)

                    if (!extraData) {
                        return
                    }

                    setMaterials(prev => {
                        const updated = [...prev];

                        updated[index] = {
                            ...updated[index],
                            material_x_storage_id: filteredValues[0],
                            ...extraData
                        }

                        return updated;
                    });
                } finally {
                    setIsLoading(false)
                }
            }, 500)
        } else {
            setMaterials(prev => ([...prev, initialMaterial]))
            setIsLoading(false)
        }
    }

    const handleUserCanCaution = async (is_gun) => {
        if (!is_gun || confirmCanCautionWithExpiredDocument) {
            return true
        }

        const {carryDueDate} = await userService.conductorDocumentStatus(userSelected?.value)

        if (!carryDueDate) {
            return true
        }

        const carryDueDateEnd = endOfDay(carryDueDate)
        const todayStart = startOfDay(new Date())
        const isExpired = !isAfter(carryDueDateEnd, todayStart)

        if (!isExpired) {
            return true
        }

        return new Promise((resolve) => {
            setIsLoading(false)
            projectUtils.swalMessage(
                'warning',
                'Atenção',
                `Agente possui porte de arma vencido, deseja continuar?`,
                'Sim',
                true,
                'Não',
                ({isConfirmed}) => {
                    if (isConfirmed) {
                        setIsLoading(true)
                    }
                    resolve(isConfirmed)
                }
            );
        })
    }

    const onChange = async (index, field, value) => {
        setIsLoading(true)
        try {
            let extraData = {};

            if (field === "material_x_storage_id" && value) {
                extraData = await handleMaterial(value, materials[index].quantity)

                if (!extraData) {
                    setMaterials(prev => {
                        const updated = [...prev];
                        updated[index] = {...initialMaterial}
                        return updated;
                    });
                    return
                }
            }

            setMaterials(prev => {
                const updated = [...prev];

                updated[index] = {
                    ...updated[index],
                    [field]: value,
                    ...extraData
                }

                return updated;
            });
        } finally {
            setIsLoading(false)
        }
    }

    const handleMaterial = async (materialXStorage, quantity) => {
        const { unique, material } = await materialXStorageService.getById(materialXStorage.value);
        if (material.is_gun) {
            const canProceed = await handleUserCanCaution(true)
            if (!canProceed) {
                return null
            }
            confirmCanCautionWithExpiredDocument = true
        }

        const isUnique = material?.is_unique ?? "";

        const alertQuantity = await handleAlert(materialXStorage, quantity)

        return  {
            is_unique: isUnique,
            is_caution: material?.is_caution ?? "",
            breakdown: unique?.breakdown ?? "",
            quantity: isUnique ? 1 : "",
            alertQuantity
        }
    }

    const handleQuantity = (index, value) => {
        setIsLoading(true)

        handleAlert(materials[index].material_x_storage_id, value)
            .then((data) => {
                setMaterials(prev => {
                    const updated = [...prev];

                    updated[index] = {
                        ...updated[index],
                        alertQuantity: data
                    }

                    return updated;
                });
            })
            .finally(() => setIsLoading(false))
    }

    const handleAlert = async (material, quantity) => {
        if (!material?.value) return null

        const formData = new FormData();
        formData.append("material_x_storage", material.value);
        formData.append("storage", storageSelected.value);
        formData.append("quantity", quantity);

        return materialAlertService.validate(formData)
    }

    const openBreakdown = (breakdown) => {
        projectUtils.swalMessage("", "", breakdown, 'Ok', false, 'Não', undefined, false, "");
    }

    const removeMaterial = (index) => {
        setMaterials(prev => prev.filter((_, i) => i !== index));
    }

    const save = () => {
        const invalidRequiredFields = markRequiredFields();
        if (invalidRequiredFields?.length) {
            projectUtils.swalErrorMessage('', "Preencha os campos obrigatórios.", 'Ok');
            return;
        }

        setIsLoading(true);
        const currentPassword = passwordInputRef.current?.value ?? password;

        const formData = new FormData()

        if (removeByThirdParty) {
            if (!thirdPartyRequester) {
                setIsLoading(false);
                projectUtils.swalErrorMessage('', "Selecione um terceiro para efetuar a entrega.", 'Ok');
                return;
            }

            formData.append('third-party-requester', thirdPartyRequester.value);
        }

        const materialsToSend = materials.filter(m => m.material_x_storage_id).map(m => ({...m, material_x_storage_id: m.material_x_storage_id.value}))

        formData.append('materials-caution', JSON.stringify(materialsToSend));
        formData.append('requester', userSelected.value);
        formData.append('storage-selected', storageSelected.value);
        formData.append('password', currentPassword);

        materialMovementService.save(formData)
            .then(() => {
                projectUtils.success(false, "Retirado com sucesso")
                refetchCautions()
                setTimeout(() => setOpenModal(false), 1000)
            })
            .catch((error) => {
                console.error(error);
                let message = 'Não foi possível salvar.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                projectUtils.swalErrorMessage('', message, 'Ok');
            })
            .finally(() => setIsLoading(false))
    }

    useGlobalKey("Enter", () => {
        if (openModal) saveButtonRef.current?.click()
    })

    const hasPermission = useHasPermission()
    const removedByThirdPerson = hasPermission(PermissionEnum.MATERIALS_REMOVED_BY_THIRD_PERSON)

    return (
        <>
            <button onClick={showModal} className={"w-[30%] m-2 flex items-center justify-center rounded-[5px] h-[70px] bg-[#39c0ed] border-[#23c6c8] text-white cursor-pointer hover:bg-[#4ecaf3]"}>
                <FaRotateRight size={25}/>
                <span className={"ml-2 text-[18px] uppercase font-bold"}>Entrega</span>
            </button>

            <Modal alignTop={true} open={openModal} setOpen={setOpenModal} overflow={true} size="md" backdrop="static" keyboard={false} title={"Entrega"} className={"w-11/12"}>
                <Modal.Body className="p-2 overflow-auto max-h-[80vh]">
                    <div className="grid grid-cols-12 gap-4 mb-4">
                        <div className="col-span-6 flex gap-4">
                            <SelectComponent
                                classNameWrapper={"w-full"}
                                id="kits"
                                value={kitSelected}
                                options={kits || []}
                                label={"Kits"}
                                isMulti
                                onChange={(value) => setKitSelected(value)}
                            />

                            <Button id={"add-kit"} onClick={addKit}>
                                <FaPlus />
                            </Button>
                        </div>
                        <div className={"col-span-6"}></div>
                    </div>

                    {
                        materials.map((m, index) => {
                            return (
                                <SentryRow key={index} className={"mb-4"}>
                                    <div className="col-span-5">
                                        <SelectComponent
                                            name="material_x_storage_id"
                                            value={m.material_x_storage_id?.value || ""}
                                            options={getMaterialOptions(m.material_x_storage_id)}
                                            label={"Material/Grupo"}
                                            onChange={(selected) => onChange(index, "material_x_storage_id", selected) }
                                        />
                                    </div>

                                    <div className="col-span-2 relative">
                                        <InputForm
                                            disabled={m.is_unique}
                                            inputClass={m.alertQuantity === "warning" ? "text-orange-500" : m.alertQuantity === "danger" ? "text-red-500" : ""}
                                            labelClass={m.alertQuantity === "warning" ? "text-orange-500" : m.alertQuantity === "danger" ? "text-red-500" : ""}
                                            id={`quantity-${index}`}
                                            type={"number"}
                                            value={m.quantity || ""}
                                            label="Quantidade *"
                                            name={"quantity"}
                                            required
                                            onChange={(e) => onChange(index, "quantity", e.target.value)}
                                            onBlur={(e) => handleQuantity(index, e.target.value)}/>

                                        {
                                            m.alertQuantity === "warning" &&
                                            <span className="absolute top-2 right-7" title="Material atingiu a quantidade mínima">
                                                <FaTriangleExclamation color={"orange"} />
                                            </span>
                                        }

                                        {
                                            m.alertQuantity === "danger" &&
                                            <span className="absolute top-2 right-7" title={`Estoque disponível: ${m.material_x_storage_id?.quantity_available}`}>
                                                <FaTriangleExclamation color={"red"}/>
                                            </span>
                                        }
                                    </div>

                                    <div className="col-span-2">
                                        <InputForm
                                            id={`observations-${index}`}
                                            value={m.observations || ""}
                                            label="Observação inicial"
                                            name={"observations"}
                                            onChange={(e) => onChange(index, "observations", e.target.value)}/>
                                    </div>

                                    <div className="col-span-3">
                                        <div className={"flex gap-4"}>
                                            <InputForm
                                                classNameWrapper={"w-full"}
                                                disabled={!m.breakdown}
                                                onClick={() => openBreakdown(m.breakdown)}
                                                inputClass={"cursor-pointer hover:underline"}
                                                readOnly={true}
                                                id={`breakdown-${index}`}
                                                value={m.breakdown || ""}
                                                label="Avaria"
                                                name={"breakdown"}
                                                onChange={(e) => onChange(index, "breakdown", e.target.value)}/>

                                            <InputCheckbox
                                                disabled={!m.is_caution}
                                                id="permanent"
                                                checked={m.permanent}
                                                label="Permanente"
                                                onChange={({target: {checked}}) => onChange(index, "permanent", checked)}/>

                                            <Button target={"danger"} onClick={() => removeMaterial(index)}>
                                                <FaTrash />
                                            </Button>
                                        </div>
                                    </div>
                                </SentryRow>
                            )
                        })
                    }

                    <div className="grid grid-cols-12 gap-4">
                        <div className={"col-span-2"}>
                            <Button id={"add-material"} onClick={addMaterial}>
                                <FaPlus /> Adicionar
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className={"p-1"}>
                    <div className={"w-full flex justify-between"}>
                        <div className={"flex gap-4 w-6/12"}>
                            {
                                removedByThirdPerson &&
                                <>
                                    <InputCheckbox
                                        className={"w-[200px]"}
                                        id="third-party-checkbox"
                                        checked={removeByThirdParty}
                                        label="Entrega por terceiro"
                                        onChange={({target: {checked}}) => setRemoveByThirdParty(checked)}/>

                                    <SelectComponent
                                        isDisabled={!removeByThirdParty}
                                        classNameWrapper={"w-full"}
                                        className={"bg-white"}
                                        name="third-party-requester"
                                        value={thirdPartyRequester}
                                        options={dataThirdPartyRequester}
                                        label={removeByThirdParty ? "Agente *" : "Agente"}
                                        onChange={(selected) => setThirdPartyRequester(selected) }
                                    />
                                </>
                            }
                        </div>

                        <div className={"flex gap-4"}>
                            <div className={"flex"}>
                                <div className={"min-w-[100px] bg-[#e0e0e0] border border-[#b2b2b2] h-9 p-2 -mr-px"}>
                                    <p>{thirdPartyRequester?.label || userSelected?.label || ""}</p>
                                </div>
                                <InputForm
                                    required
                                    inputClass={"bg-white !h-9 !border-[#b2b2b2] !rounded-[0px] !w-[100px]"}
                                    label={"Senha/PIN *"}
                                    id={`password`}
                                    type={"password"}
                                    value={password}
                                    name={"password"}
                                    inputRef={passwordInputRef}
                                    onChange={({target: {value}}) => setPassword(value)}/>
                            </div>
                            <Button
                                reference={saveButtonRef}
                                target={"info"}
                                onClick={save}
                                actionFeedbackType="save"
                                loadingText="Retirando..."
                                successText="Retirado com sucesso"
                                className={"!h-9"}
                            >
                                Retirar
                            </Button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default Caution;
